import { z } from "zod";

export const monthlyDataSchema = z.object({
  year: z.number(),
  month: z.number().min(1).max(12),
  monthName: z.string(),
  sales: z.number(),
  income: z.number(),
});

export const agingBucketSchema = z.object({
  bucket: z.enum(["under30", "between30And90", "over90"]),
  count: z.number(),
  total: z.number(),
});

export const upcomingDueInvoiceSchema = z.object({
  id: z.string(),
  invoiceNumber: z.string(),
  relationName: z.string(),
  dueDate: z.date(),
  total: z.number(),
});

export const topClientSchema = z.object({
  relationId: z.string(),
  name: z.string(),
  total: z.number(),
});

export const dashboardStatsSchema = z.object({
  totalInvoices: z.number(),
  overdue: z.object({
    count: z.number(),
    total: z.number(),
  }),
  notSent: z.object({
    count: z.number(),
    total: z.number(),
  }),
  sent: z.object({
    count: z.number(),
    total: z.number(),
  }),
  partiallyPaid: z.object({
    count: z.number(),
    totalPaid: z.number(),
    totalAmount: z.number(),
  }),
  sales: z.object({
    count: z.number(),
    currentMonth: z.number(),
    previousMonth: z.number(),
    currentYear: z.number(),
    previousYear: z.number(),
  }),
  income: z.object({
    count: z.number(),
    currentMonth: z.number(),
    previousMonth: z.number(),
    currentYear: z.number(),
    previousYear: z.number(),
  }),
  benefits: z.object({
    currentMonth: z.number(),
    previousMonth: z.number(),
    currentYear: z.number(),
    previousYear: z.number(),
  }),
  last12Months: z.array(monthlyDataSchema),
  // Outstanding invoices bucketed by how long they have been overdue
  aging: z.array(agingBucketSchema),
  // Invoices falling due within the next 7 days
  upcomingDue: z.object({
    total: z.number(),
    invoices: z.array(upcomingDueInvoiceSchema),
  }),
  // Highest revenue clients for the current year
  topClients: z.array(topClientSchema),
  currency: z.string(),
});

export type MonthlyData = z.infer<typeof monthlyDataSchema>;
export type DashboardStats = z.infer<typeof dashboardStatsSchema>;
export type AgingBucket = z.infer<typeof agingBucketSchema>;
export type UpcomingDueInvoice = z.infer<typeof upcomingDueInvoiceSchema>;
export type TopClient = z.infer<typeof topClientSchema>;
